import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  IndianRupee,
  Calendar,
  Download,
  RefreshCw,
  ArrowUpRight,
  ArrowDownRight,
  DollarSign
} from 'lucide-react';
import api from '../../services/api.js';

const AnalyticsRevenue = () => {
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30');

  useEffect(() => {
    fetchAnalytics();
  }, [period]);

  const fetchAnalytics = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/analytics?period=${period}`);
      if (response.data.success) {
        setAnalytics(response.data.data);
      }
    } catch (error) {
      console.error('Error fetching analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const RevenueCard = ({ title, value, change, icon: Icon, color = 'primary' }) => {
    const isPositive = change >= 0;
    const colorClasses = {
      primary: 'bg-primary-500',
      green: 'bg-green-500',
      blue: 'bg-blue-500',
      purple: 'bg-purple-500'
    };

    return (
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">{title}</p>
            <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
            {change !== undefined && (
              <div className={`flex items-center mt-2 text-sm ${
                isPositive ? 'text-green-600' : 'text-red-600'
              }`}>
                {isPositive ? (
                  <ArrowUpRight className="w-4 h-4 mr-1" />
                ) : (
                  <ArrowDownRight className="w-4 h-4 mr-1" />
                )}
                <span>{Math.abs(change)}% from last period</span>
              </div>
            )}
          </div>
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            <Icon className="w-6 h-6 text-white" />
          </div>
        </div>
      </div>
    );
  };

  const RevenueChart = ({ data, title }) => {
    if (!data || data.length === 0) {
      return (
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
          <div className="text-center py-8 text-gray-500">No data available</div>
        </div>
      );
    }

    const maxValue = Math.max(...data.map(d => d.revenue || 0));

    return (
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">{title}</h3>
        <div className="space-y-3">
          {data.slice(0, 10).map((item, index) => {
            const percentage = maxValue > 0 ? (item.revenue / maxValue) * 100 : 0;
            
            return (
              <div key={index} className="flex items-center">
                <div className="w-20 text-sm text-gray-600">
                  {item.date ? new Date(item.date).toLocaleDateString() : `Day ${index + 1}`}
                </div>
                <div className="flex-1 mx-3">
                  <div className="bg-gray-200 rounded-full h-3">
                    <div 
                      className="bg-green-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
                <div className="w-24 text-sm font-medium text-gray-900 text-right">
                  {formatCurrency(item.revenue)}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading revenue analytics...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center">
              <IndianRupee className="w-8 h-8 mr-3 text-primary-600" />
              Revenue Analytics
            </h1>
            <p className="text-gray-600 mt-1">Detailed revenue insights and trends</p>
          </div>
          <div className="flex space-x-3">
            <select
              value={period}
              onChange={(e) => setPeriod(e.target.value)}
              className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-primary-500"
            >
              <option value="7">Last 7 days</option>
              <option value="30">Last 30 days</option>
              <option value="90">Last 90 days</option>
              <option value="365">Last year</option>
            </select>
            <button
              onClick={fetchAnalytics}
              className="inline-flex items-center px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </button>
            <button className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-50">
              <Download className="w-4 h-4 mr-2" />
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Revenue Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <RevenueCard
          title="Total Revenue"
          value={formatCurrency(analytics?.revenue?.total || 0)}
          change={12.5}
          icon={IndianRupee}
          color="green"
        />
        <RevenueCard
          title="Average Order Value"
          value={formatCurrency((analytics?.revenue?.total || 0) / (analytics?.orders?.total || 1))}
          change={8.2}
          icon={DollarSign}
          color="blue"
        />
        <RevenueCard
          title="Monthly Recurring Revenue"
          value={formatCurrency((analytics?.revenue?.total || 0) / 12)}
          change={15.3}
          icon={TrendingUp}
          color="purple"
        />
        <RevenueCard
          title="Revenue Growth Rate"
          value="12.5%"
          change={5.7}
          icon={BarChart3}
          color="primary"
        />
      </div>

      {/* Revenue Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        <RevenueChart
          title="Daily Revenue Trend"
          data={analytics?.revenue?.daily || []}
        />
        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Breakdown</h3>
          <div className="space-y-4">
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-sm font-medium text-gray-700">Product Sales</span>
              <span className="text-sm font-bold text-gray-900">
                {formatCurrency((analytics?.revenue?.total || 0) * 0.85)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-sm font-medium text-gray-700">Service Fees</span>
              <span className="text-sm font-bold text-gray-900">
                {formatCurrency((analytics?.revenue?.total || 0) * 0.10)}
              </span>
            </div>
            <div className="flex justify-between items-center p-3 bg-gray-50 rounded">
              <span className="text-sm font-medium text-gray-700">Shipping</span>
              <span className="text-sm font-bold text-gray-900">
                {formatCurrency((analytics?.revenue?.total || 0) * 0.05)}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Revenue Insights */}
      <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Revenue Insights</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="text-center p-4 bg-green-50 rounded-lg">
            <TrendingUp className="w-8 h-8 text-green-600 mx-auto mb-2" />
            <h4 className="font-semibold text-green-900">Strong Growth</h4>
            <p className="text-sm text-green-700 mt-1">
              Revenue has grown consistently over the past {period} days
            </p>
          </div>
          <div className="text-center p-4 bg-blue-50 rounded-lg">
            <BarChart3 className="w-8 h-8 text-blue-600 mx-auto mb-2" />
            <h4 className="font-semibold text-blue-900">Peak Performance</h4>
            <p className="text-sm text-blue-700 mt-1">
              Highest revenue days typically occur mid-week
            </p>
          </div>
          <div className="text-center p-4 bg-purple-50 rounded-lg">
            <IndianRupee className="w-8 h-8 text-purple-600 mx-auto mb-2" />
            <h4 className="font-semibold text-purple-900">Revenue Optimization</h4>
            <p className="text-sm text-purple-700 mt-1">
              Focus on high-value products for better margins
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnalyticsRevenue;
